<template>
  <div class="table-outer">
    <div class="table-top-area">
      <span 
        v-if="totalRecords"
        class="total-records" 
      >
        {{ $tc('articles.total_record', totalRecords, { total: totalRecords }) }}
      </span>
      <span
        v-if="genericRecords"
        class="total-records">
        {{ $tc('articles.generic_record', genericRecords, { total: genericRecords }) }}
      </span>
    </div>
    <div ref="datatableWrapper" @scroll="setTableWrapperScroll" :class="{
    'table-wrapper': true, 
    'table-wrapper--overflow-sticky-header': this.data.stickyHeader && mergedData.fixedColumns.enabled && !loading && rows.length !== 0, 
    'table-wrapper--overflow': !this.data.stickyHeader && mergedData.fixedColumns.enabled, 
    'table-wrapper--empty': (rows.length === 0 || loading), 
    'table-wrapper--loading': loading}">
      <table ref="datatable" :class="[...tableClasses, { 'dragging': this.draggedData }]">
        <thead v-if="!hideHeader">
          <tr>
            <th v-if="reorderRowsEnabled" :style="{ width: '20px', left: '0' }" class="p-0"></th>
            <th v-if="mergedData.bulkActions.enabled" ref="column_select_all" :style="bulkActionsColumnStyle">
              <input-checkbox :onChange="onSelectAllChange" :value="rows.length > 0 && selectedRows.length == rows.length" :index="0" id="all" :disabled="rows.length === 0 || loading" />
            </th>
            <th
              v-for="(column, index) in mergedData.columns.filter(c => toggleColumns.enabled ? !toggleColumns.hidden.includes(c.key) : true)"
              :key="index"
              :ref="`column_${column.key}`"
              :class="[orderClass(column), column.class, (column.filter ? 'filterable' : ''), (column.filter && appliedFilters[column.filter.name] ? 'filterable--filtered': '')].join(' ')"
              :data-column="column.key"
              :style="column.style && Object.entries(column.style).map(k => `${k[0]}: ${k[1]}`).join('; ')"
              >
              <div class="cell-content">
                <span class="text" @click="() => {
                    if (column.filter) {
                      onDisplayFilters(column.key)
                    }
                  }">
                  <slot v-if="column.filter && appliedFilters[column.filter.name]">
                    {{appliedFilters[column.filter.name].label}}
                  </slot>
                  <slot v-else>{{column.label}}</slot>
                  <i v-if="column.tooltip" data-tip class="fas fa-info-circle" v-tooltip="column.tooltip"></i>
                </span>

                <div class="actions">
                  <span v-if="column.orderable == true" :class="{ 'disabled': (rows.length === 0 || loading) }" data-action="sort" v-on:click="reorder(column)" v-tooltip="column.sortTooltip"></span>
                </div>

              </div>
              <template v-if="tableFilters && column.filter && column.filter.name && tableFilters[column.filter.name] && showColumnFilter == column.key">
                <datatable-header-filter
                :ref="`datatable-header-filter-${column.key}`"
                :title="tableFilters[column.filter.name].title"
                :searchOptions="tableFilters[column.filter.name].searchOptions"
                :options="tableFilters[column.filter.name].options"
                :onFilter="(option) => {

                  tableFilters[column.filter.name].onFilter(option);
                  // applyFilterLabel(column.filter.name, option);
                }"
                :onClear="(value) => {tableFilters[column.filter.name].onClearHeader(value)}"
                :placeholder="tableFilters[column.filter.name].placeholder"
                :onClose="onClickOutsideColumnFilters"
                :applied="appliedFilters[column.filter.name]"
                v-click-outside="onClickOutsideColumnFilters"
                />
              </template>
            </th>
            <th v-if="toggleColumns.enabled" class="toggle-columns-wrapper">
              <div class="cell-content">
                <button ref="addColumn" :disabled="rows.length === 0 || loading" v-on:click.stop.prevent="onDisplayToggleColumns" class="add-column">
                  <i class="fas fa-ellipsis-h"></i>
                </button>
              </div>
            </th>
            <th v-if="mergedData.removeRow.enabled === true"></th>
          </tr>
        </thead>
        <tbody v-if="loading === false || (loading && keepDataVisibleOnLoad)">

          <template v-for="(row, rowIndex) in rows">
          <tr
            :key="`${row.id}-${rowIndex}`"
            :class="getRowClasses(row)"
            @click="(event) => onRowClick(event, row)"
            @dragenter="(e) => onDragRowEnter(e, row, rowIndex)"
            @dragover.prevent
          >
            <td v-if="reorderRowsEnabled"><div class="row-drag-handler opacity-25 cursor-pointer text-center" :draggable="reorderRowsEnabled" @dragstart="(e) => onDragRowStart(e, row, rowIndex)" @dragend="onDragRowEnd"><i class="fas fa-grip-vertical"></i></div></td>
            <td v-if="mergedData.bulkActions.enabled" :style="bulkActionsColumnStyle" @click.stop>
              <div class="cell-content">
                <input-checkbox 
                  :disabled="mergedData.bulkActions.enabled && ((mergedData.bulkActions.disableKey) ? !typeof row[mergedData.bulkActions.disableKey] === 'boolean' || row[mergedData.bulkActions.disableKey] === true : false)" 
                  :onChange="(val) => onSelectRowChange(val, row)" 
                  :index="rowIndex" id="row.selected" 
                  :value="selectedRows.map(r=>r.id).includes(row.id)" />
              </div>
            </td>
            <td v-for="(column, columnIndex) in mergedData.columns.filter(c => !toggleColumns.hidden.includes(c.key))"
              :key="columnIndex"
              :ref="`td_${rowIndex}_${columnIndex}`"
              :class="column.class"
              :style="column.cellStyle && Object.entries(column.cellStyle).map(k => `${k[0]}: ${k[1]}`).join('; ')"
              >
              <datatable-custom-cell v-if="column.render" :style="cellContentStyles[columnIndex]" :render="() => column.render(row, rowIndex)"/>
              <div v-else-if="column.input" class="cell-content" :style="cellContentStyles[columnIndex]" :ref="`cell_content_${rowIndex}_${columnIndex}`">
                <input-select v-if="column.input.type == FIELD_TYPE.SELECT" v-bind="propsFor('InputSelect', column.disableKey ? disableInput(column.disableKey, row , column.input) : column.input)" :name="'row_' + row.id + '_' + column.input.name" :onChange="(value) => { column.input.onChange(value, column.input.name, row) }" :selectedValue="row[column.input.name]" :class="{ 'datatable-input-select': true, 'datatable-input-select--fixed': (mergedData.fixedColumns.enabled && !column.input.disableFixedPosition) }" />
                <input-checkbox v-else-if="column.input.type == FIELD_TYPE.CHECKBOX" v-bind="propsFor('InputCheckbox', column.disableKey ? disableInput(column.disableKey, row , column.input) : column.input)" :onChange="(value) => { column.input.onChange(value, column.input.name, row) }" class="datatable-input-checkbox" :index="rowIndex" :value="row[column.key]" />
                <input-toggle v-else-if="column.input.type == FIELD_TYPE.TOGGLE" v-bind="propsFor('InputToggle', column.input)" :onChange="(value) => { column.input.onChange(value, column.input.name, row) }" class="datatable-input-toggle" :index="rowIndex" :checked="row[column.key]"/>
                <input-text v-else-if="column.input.type == FIELD_TYPE.INPUT" v-bind="propsFor('InputText', column.disableKey ? disableInput(column.disableKey, row , column.input) : column.input)" :onChange="(value) => { column.input.onChange && column.input.onChange(value, column.input.name, row, parameters) }" :onBlur="(value) => { column.input.onBlur && column.input.onBlur(value, column.input.name, row) }" :value="row[column.key]" :class="{ 'datatable-input-text': true }">
                  <template v-if="isUpdatingRow(row.id)" v-slot:afterField>
                    <span class="spinner-border spinner-border-sm d-inline-block" role="status" aria-hidden="true"></span>
                  </template>
                  <template v-else v-slot:afterField>
                    <span class="d-inline-block" style="width: 1rem;"></span>
                  </template>
                </input-text>
                <input-text
                  v-else-if="column.input.type == FIELD_TYPE.NUMBER"
                  v-bind="propsFor('InputText', column.disableKey ? disableInput(column.disableKey, row , column.input) : column.input)" :onChange="(value) => { column.input.onChange && column.input.onChange(value, column.input.name, row, parameters) }"
                  :onBlur="(value) => { column.input.onBlur && column.input.onBlur(value, column.input.name, row) }"
                  :value="row[column.key]"
                  :min="column.input.min"
                  :class="{ 'datatable-input-text': true }"
                >
                  <template v-if="isUpdatingRow(row.id)" v-slot:afterField>
                    <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  </template>
                  <template v-else v-slot:afterField>
                    <span class="d-inline-block" style="width: 1rem;"></span>
                  </template>
                </input-text>
                <input-button v-else-if="column.input.type == FIELD_TYPE.BUTTON" v-bind="propsFor('InputButton', column.input)" :onClick="() => { column.input.onClick && column.input.onClick(column.input.name, row, parameters) }" :value="row[column.key]" :class="{ 'datatable-input-button': true }"/>
                <color-picker v-else-if="column.input.type == FIELD_TYPE.COLOR" v-bind="column.input" :onChange="(value) => { column.input.onChange(value, column.input.name, row) }" :value="row[column.key]" :class="{ 'datatable-color-picker': true, 'datatable-color-picker--fixed': mergedData.fixedColumns.enabled }" />
                <datepicker v-else-if="column.input.type == FIELD_TYPE.CALENDAR" v-bind="propsFor('Datepicker', column.input)" :onChange="(value) => { column.input.onChange(value, column.input.name, row) }" :value="row[column.key]" :class="{ 'datatable-datepicker': true, 'datatable-datepicker--fixed': mergedData.fixedColumns.enabled }" />
                <component v-else :is="customComponents[column.input.component.name]" :getProps="() => column.input.component.getProps(row)" v-bind="column.input.component" />
              </div>
              <div v-else class="cell-content" :style="cellContentStyles[columnIndex]" v-html="displayValue(row[column.key])"></div>
            </td>
           <td v-if="mergedData.removeRow.enabled && ((mergedData.removeRow.disableKey) ? !typeof row[mergedData.removeRow.disableKey] === 'boolean' || row[mergedData.removeRow.disableKey] === false : true)" class="remove-icon">
              <i
                class="remove-item fas fa-minus-circle"
                @click="removeRow(row)"
                v-tooltip="$t('form.button.remove')"
              ></i>
            </td>
          </tr>
          <tr
            v-if="isRowExpanded(row)"
            :key="`${row.id}-${rowIndex}-expand`"
            class="datatable-expand-row"
          >
            <td :colspan="expandRowColspan">
              <slot name="expand" :row="row" :rowIndex="rowIndex" />
            </td>
          </tr>
          </template>
        </tbody>
      </table>

      <div v-if="addButtonEnabled && !loading" class="add-org">
        <span class="span-add-row" @click="addRow">
          <i class="fas fa-plus btn-primary" ></i> <b>{{mergedData.addRow.label}}</b>
        </span>
      </div>
    </div>

    <div ref="toggleColumns" v-if="showToggleColumns" id="toggle-columns" :style="toggleColumnsStyle" v-click-outside="onClickOutsideToggleColumns">
      <div class="title">{{$t("articles.hide_show_columns")}}</div>

      <ul class="draggable-list">
        <li v-for="(column, index) in mergedData.columns.filter(c => toggleColumns.enabled && !toggleColumns.excluded.includes(c.key))" :key="index" 
          draggable="true"
          @dragstart="(e) => onTableColDragStart(index, e)"
          @dragover.prevent="(e) => onTableColDragOver(index, e)"
          @drop="(e) => onTableColDrop(index, e)"
        >
          <div class="d-flex justify-content-between">
            <div>
              <span :data-column="column.key" :class="toggleColumns.enabled && toggleColumns.hidden.includes(column.key) ? '' : 'active'" 
                v-on:click="() => onClickToggleColumn(column.key)">
                {{column.label}}
              </span>
            </div>
            <div>
              <i class="fa fa-bars table-col-drag-handle"/>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <loader v-if="loading" :wrapperStyle="{ height: '100px' }" :class="{ 'absolute-center': keepDataVisibleOnLoad }" />

    <div v-if="rows.length == 0 && loading === false">
      <div class="empty-state">
        <div class="icon"><i class="fas fa-search"></i></div>
        <div>{{noResults}}</div>
      </div>
    </div>
  </div>

</template>


<script>

import _ from 'lodash';
import { mapActions, mapState } from 'vuex';
import i18n from '../i18n';
import InputButton from './form-components/input-button';
import InputSelect from './form-components/input-select';
import InputCheckbox from './form-components/input-checkbox';
import InputToggle from './form-components/input-toggle';
import InputText from './form-components/input-text';
import Datepicker from '../components/datepicker';
import ColorPicker from '../components/color-picker';
import { FIELD_TYPE, EMPTY_IMAGE, FIXED_TABLE_COLUMNS } from '../config/const';
import { propsForMixin } from '../helpers/application';
import { valueFromString } from '../helpers/object';
import DatatableHeaderFilter from '../components/datatable-header-filter';
import DatatableCustomCell from '../components/datatable-custom-cell';
import Loader from '../components/loader';
import resizableTable from "../mixins/datatables/resizableTable";

const bulkActionsColumnWidth = 50;

export default {
  props: {
    data: {
      type: Object,
      required: true,
      default: function () {
        return {};
      }
    },
    keepDataVisibleOnLoad: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    noResults: {
      type: String,
      default: i18n.t('datatable.empty_results')
    },
    labelAdd: {
      type: String,
      default: 'Add',
    },
    parameters: {
      type: Object,
      default: function() {
        return {};
      },
    },
    updatingRow:  {
      type: [Number, String, Array],
      default: null,
    },
    totalRecords:  {
      type: Number,
      required: false,
      default: undefined,
    },
    genericRecords: {
      type: Number,
      required: false,
      default: undefined,
    },
    overTotalLimit:  {
      type: Boolean,
      required: false,
      default: false,
    },
    maxToList: {
      type: [Number, String],
      required: false,
      default: 100,
    },
    code: {
      type: String,
      require: false,
      default: ''
    },
    savePreferences: {
      type: Boolean,
      require: false,
      default: false
    },
    bulkEdit: Boolean, // don't deselected selectedRows on bulk when loading pagination
    hideHeader: {
      type: Boolean,
      default: false,
    },

  },

  components: {
    Loader,
    InputSelect,
    InputCheckbox,
    InputToggle,
    InputText,
    ColorPicker,
    Datepicker,
    DatatableHeaderFilter,
    InputButton,
    DatatableCustomCell
  },

  mounted() {
    this.init();
    this.setTableWrapperWidth(true);
  },

  updated () {
    this.reattachDragEventListeners();
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.setTableWrapperWidth);
    this.$refs.datatableWrapper.removeEventListener('scroll', this.setTableWrapperScroll);
  },

  data: function() {

    const defaultData = {
      custom_components: {},
      columns: [],
      body: [],
      order: {},
      colReorder: {
        enabled: false,
        disabledColumnsLeft: 0
      },
      fixedColumns: {
        enabled: false,
        fixedColumnsLeft: 0,
        widths: [],
        class: 'fixedColumn'
      },
      toggleColumns: {
        enabled: false,
        excluded: [],
        hidden: []
      },
      bulkActions: {
        enabled: false,
        disableKey: null,
        onSelect: function(value) { // now receives row.id instead of row
          return value;
        }
      },
      removeColumn: {
        enabled: true
      },
      addRow: {
        enabled: false,
        label: 'Add row',
        onClick: () => null
      },
      removeRow: {
        enabled: false,
        disableKey: null,
        onClick: () => null
      },
      rowClass: (row) => {
        return '';
      },
      expandableRows: {
        enabled: false,
        expandedId: null,
        getRowId: (row) => row.id,
        single: true,
        onToggle: () => null,
        rowClass: () => '',
        shouldIgnoreClick: null,
      },
      dragIndex: null,
      dragOverIndex: null,
    }

    const mergedData = _.merge(defaultData, this.data);
    // const mergedData = Object.assign({}, defaultData, this.data);

    const custom_components = {};
    for (const c in mergedData.custom_components) {
      custom_components[c] = mergedData.custom_components[c];
    }

    const rows = mergedData.body;

    // apply static filters
    // let appliedFilters = {};
    // mergedData.columns.forEach(c => {
    //   if (c.filter && c.filter.applied) {
    //     appliedFilters[c.filter.name] = c.filter.applied;
    //     // this.filterRows(c.filter.name, c.filter.applied);
    //   }
    // })

    const columnsOrder = mergedData.columns.map(c => c.key);

    let columnsOffsetLeft = [];
    if (mergedData.bulkActions.enabled) {
      columnsOffsetLeft.push({ type: 'select_all', width: bulkActionsColumnWidth });
    }

    const reorderRowsEnabled = this.data.reorderRows?.enabled === true && !this.data.columns.find(c => c.orderable === true);
    if (reorderRowsEnabled) {
      columnsOffsetLeft.push({ width: 50 });
    }

    return {
      tableClasses: this.data.stickyHeader ? ['table', 'table--datatable','sticky-header'] : ['table', 'table--datatable'],
      mergedData: mergedData,
      resizableTable: null,
      preferences: {
        widths: []
      },
      // rows,
      FIELD_TYPE,
      customComponents: custom_components,
      showToggleColumns: false,
      toggleColumns: mergedData.toggleColumns,
      toggleColumnsStyle: '',
      selectedRows: [],
      showColumnFilter: '',
      // appliedFilters,
      columnsOffsetLeft,
      fixedColumsPositions: {},
      columnsOrder,
      bulkActionsColumnStyle: {
        'left': 0 + 'px',
        'width': bulkActionsColumnWidth + 'px',
        'min-width': bulkActionsColumnWidth + 'px',
        'max-width': bulkActionsColumnWidth + 'px',
      },
      tableWrapperWidth: 0,
      
      // Table rows reorder data
      reorderRowsEnabled,
      draggedData: null,
      rowsIndexes: {},
      dragIndex: null,
      dragOverIndex: null
    }
  },

  computed: {
    ...mapState({
      userPreferences: 'user/preferences',
    }),

    rows () {
      return (this.data.body || []).map((row, index) => {
        const rowId = 'row-' + index;
        return { ...row, row_id: rowId, index: typeof this.rowsIndexes[rowId] === 'undefined' ? index : this.rowsIndexes[rowId] };
      }).sort((a, b) => {
        return a.index > b.index ? 1 : -1;
      });
    },

    totalRecordsInfo() {
      if (this.totalRecords && !this.overTotalLimit)
        return this.$tc('articles.total_record', this.totalRecords, { total: this.totalRecords });

      if (this.overTotalLimit) {
        let formatter = Intl.NumberFormat('en', { notation: 'compact' });
        return this.$t('articles.total_record_more_than', { max: formatter.format(this.maxToList) });
      }

      return '';
    },
    isSelected(row){
      return this.selectedRows.map(r=>r.id).includes(row.id);
    },

    appliedFilters() {
      let applied = {};
      this.data.columns.forEach(c => {
        if (c.filter && c.filter.applied) {
          applied[c.filter.name] = c.filter.applied;
        }
      })
      return applied;
    },

    cellContentStyles() {
      let cols = this.mergedData.columns
      let cellContentStyle = []
      if(cols && cols.length) {
        cellContentStyle = cols.map(col => col.cellContentStyle && Object.keys(col.cellContentStyle).map(key => `${[key]}: ${col.cellContentStyle[key]}`).join('; '))
      }
      return cellContentStyle
    },

    addButtonEnabled() { return this.data.addRow ? this.data.addRow.enabled :  false },

    tableFilters(){
      let filters = {};
      this.data.columns.forEach(c => {
        if(c.filter && c.filter.name) filters[c.filter.name] = c.filter;
      })
      return filters;
    },

    expandableRowsConfig() {
      return {
        ...this.mergedData.expandableRows,
        ...(this.data.expandableRows || {}),
      };
    },

    expandableRowsEnabled() {
      return this.expandableRowsConfig.enabled === true;
    },

    expandRowColspan() {
      let count = this.mergedData.columns.filter((column) => !this.toggleColumns.hidden.includes(column.key)).length;

      if (this.reorderRowsEnabled) {
        count += 1;
      }

      if (this.mergedData.bulkActions.enabled) {
        count += 1;
      }

      if (this.toggleColumns.enabled) {
        count += 1;
      }

      if (this.mergedData.removeRow.enabled === true) {
        count += 1;
      }

      return count;
    },
  },

  watch: {
    columnsOrder: function(value) {
      this.mergedData.columns = value.map(col => this.mergedData.columns.find(c => c.key == col));
    },
    'data.columns': function(value) {
      this.mergedData.columns = value;
    },
    loading: function(value) {
      if (value === true) {
        this.selectedRows = [];
      }
    }
  },

  methods: {
    ...mapActions({
      updateUserPreferences: 'user/updateUserPreferences',
    }),

    init() {
      const self = this;

      const { columnsOffsetLeft, mergedData } = this;
      const { columns, /*order, */colReorder, fixedColumns, toggleColumns/*, bulkActions*/ } = mergedData;

      if (_.isEmpty(fixedColumns) == false && fixedColumns.enabled && _.isFinite(fixedColumns.fixedColumnsLeft)) {
        self.tableClasses.push('table--datatable-fixed-' + (fixedColumns.fixedColumnsLeft + columnsOffsetLeft.length));

        let leftPosition = 0;
        if (columnsOffsetLeft.length > 0) {
          for (let i = 0; i < columnsOffsetLeft.length; i++) {
            leftPosition += columnsOffsetLeft[i].width;
          }
        }

        columns.forEach((column, i) => {
          if (fixedColumns.widths[i]) {
            const width = fixedColumns.widths[i];
            let style = {
              'width': width + 'px',
              'min-width': width + 'px',
              'max-width': width + 'px'
            }

            columns[i].style = Object.assign({}, columns[i].style, style);
          }
        });

        for (let i = 0; i < fixedColumns.fixedColumnsLeft + columnsOffsetLeft.length; i++) {
          if (i + 1 != columnsOffsetLeft.length) {
            const columnName = `column_${columns[i - columnsOffsetLeft.length].key}`;
            const width = fixedColumns.widths[i - columnsOffsetLeft.length];
            let style = {
              'left': leftPosition + 'px',
              'width': width + 'px',
              'min-width': width + 'px',
              'max-width': width + 'px',
            }
            columns[i - columnsOffsetLeft.length].style = Object.assign({}, columns[i - columnsOffsetLeft.length].style, style);
            columns[i - columnsOffsetLeft.length].cellStyle = Object.assign({}, columns[i - columnsOffsetLeft.length].cellStyle, style);
            columns[i - columnsOffsetLeft.length].class = 'fixedColumn'
            leftPosition += width;
          }
        }
      }

      if (_.isEmpty(toggleColumns) == false) {
        this.toggleColumns = Object.assign({}, this.toggleColumns, { ...toggleColumns });
      }

      if(this.data.resizable) this.mountResizableTable()
    },

    isUpdatingRow(rowId){
      if(Array.isArray(this.updatingRow)) 
        return this.updatingRow.includes(rowId);
      return (this.updatingRow === rowId);
    },

    getExpandableRowId(row) {
      const { getRowId } = this.expandableRowsConfig;
      return typeof getRowId === 'function' ? getRowId(row) : row.id;
    },

    isRowExpanded(row) {
      if (!this.expandableRowsEnabled) {
        return false;
      }

      const rowId = this.getExpandableRowId(row);
      return this.expandableRowsConfig.expandedId === rowId;
    },

    getRowClasses(row) {
      const classes = [
        {
          'updating-row': this.isUpdatingRow(row.id),
          'dropped-row': this.draggedData && this.draggedData.row.row_id === row.row_id,
        },
        this.mergedData.rowClass(row),
      ];

      if (this.expandableRowsEnabled) {
        const isExpanded = this.isRowExpanded(row);
        const expandableClass = this.expandableRowsConfig.rowClass(row, isExpanded);

        if (expandableClass) {
          classes.push(expandableClass);
        }
      }

      return classes;
    },

    defaultShouldIgnoreExpandClick(event) {
      return Boolean(
        event.target.closest('input, button, a, .dropdown-button, .custom-checkbox, .content_check')
      );
    },

    onRowClick(event, row) {
      if (!this.expandableRowsEnabled) {
        return;
      }

      const shouldIgnoreClick = this.expandableRowsConfig.shouldIgnoreClick || this.defaultShouldIgnoreExpandClick;

      if (shouldIgnoreClick(event)) {
        return;
      }

      const rowId = this.getExpandableRowId(row);
      const isExpanded = this.expandableRowsConfig.expandedId === rowId;
      const { onToggle } = this.expandableRowsConfig;

      if (typeof onToggle === 'function') {
        onToggle(row, !isExpanded);
      }
    },

    disableInput(key, row, input){
      let inputProps = input;
      if(_.isEmpty(row) === false){
        if(typeof row[key] === 'boolean' && row[key] === true){
          switch (inputProps.type) {
            case FIELD_TYPE.INPUT:
              inputProps = Object.assign({}, input , {readOnly: true});
              break;
            case FIELD_TYPE.SELECT:
              inputProps = Object.assign({}, input , {disabled: true});
              break;
            case FIELD_TYPE.CHECKBOX:
              inputProps = Object.assign({}, input , {disabled: true});
              break;
            default:
              inputProps;
          }
        }
      }
      return inputProps;
    },

    reorder(column) {
      if (this.mergedData.order.column === column.key/*order.column == column.key*/) {
        this.mergedData.order.direction = this.mergedData.order.direction === 'asc' ?  'desc' : 'asc';
      }
      else {
        this.mergedData.order.direction = 'asc';
      }
      this.mergedData.order.column = column.key;
      this.mergedData.order.onChange(this.mergedData.order.column , this.mergedData.order.direction)

      if (this.savePreferences) {
        this.updateUserPreferences({
          name: this.getPreferenceCode('column_order'),
          settings: {
            order: column.key,
            sort: this.mergedData.order.direction
          }
        });
      }

      this.$emit('onSort', { order: column.key, sort: this.mergedData.order.direction });
    },

    orderClass(column) {
        return column.key == this.mergedData.order.column ? 'sorting_' + this.mergedData.order.direction : '';
    },

    onDisplayToggleColumns() {
      setTimeout(function() {
        this.showToggleColumns = !this.showToggleColumns;

        if (this.showToggleColumns) {
          this.$nextTick(function() {
            const button = this.$refs.addColumn;
            const window = this.$refs.toggleColumns;

            const left = button.getBoundingClientRect().left;
            const top = button.getBoundingClientRect().top;

            this.toggleColumnsStyle = `top: ${top + 20}px; left: ${left - window.clientWidth + button.clientWidth}px`;
          });
        }
      }.bind(this), 10);
    },

    onClickToggleColumn(col) {
      let { hidden } = this.toggleColumns;
      const visible = !hidden.includes(col);

      if (visible) {
        hidden.push(col);
      }
      else {
        hidden = hidden.filter(c => c != col);
      }

      this.toggleColumns.hidden = hidden;

      this.$emit('toggle-columns', hidden.slice());

      if (this.savePreferences) {
        this.updateUserPreferences({
          name: this.getPreferenceCode('hidden_column'),
          settings:{ hidden }
        });
      }

      if(this.resizableTable) this.mountResizableTable()
    },

    getSeparatedColumns(isFixedColumns) {
      const fixedColumns = isFixedColumns ? this.mergedData.columns.filter(col => FIXED_TABLE_COLUMNS.includes(col.key)) : [];
      const otherColumns = this.mergedData.columns.filter(col => !FIXED_TABLE_COLUMNS.includes(col.key));

      return { fixedColumns, otherColumns };
    },

    onTableColDragStart(index, event) {
      this.dragIndex = index;
      event.dataTransfer.effectAllowed = 'move';
    },

    onTableColDragOver(index, event) {
      event.preventDefault();
      if (index === this.dragOverIndex) return;

      this.dragOverIndex = index;
      const { fixedColumns, otherColumns } = this.getSeparatedColumns(true);
      const columns = [...otherColumns];
      const [moved] = columns.splice(this.dragIndex, 1);
      let targetIndex = index;
      if (this.dragIndex < index) {
        targetIndex = index - 1;
      }
      columns.splice(targetIndex, 0, moved);
      this.mergedData.columns = [...fixedColumns, ...columns];
      this.dragIndex = targetIndex;
    },

    onTableColDrop(index, event) {
      if (this.dragIndex === null) return;
      
      const { otherColumns } = this.getSeparatedColumns();
      this.dragIndex = null;
      const orderedKeys = otherColumns.map(ele=> ele.key);
      // Persist user preference
      this.updateUserPreferences({
        name: this.getPreferenceCode("ordered_columns"),
        settings: { ordered_columns: orderedKeys },
        projectId: this.$route.params.project_uid
      });

      this.mountResizableTable();
    },

    onClickOutsideToggleColumns: function() {
      this.showToggleColumns = false;
    },

    onSelectRowChange(val, row) {
      if (val == true) {
        this.selectedRows.push(row);
      }
      else {
        this.deselectRowId(row.id);
      }
      const keepSelected = this.rows.filter(r => this.selectedRows.map(sr=>sr.id).includes(r.id))
      this.mergedData.bulkActions.onSelect(keepSelected);
    },
    deselectRowId(rowid){
      this.selectedRows = this.selectedRows.filter(r=>r.id !== rowid);
    },

    onSelectAllChange(val) {
      if (val == true) {
        if(this.mergedData.bulkActions.disableKey){
          this.selectedRows = this.rows.filter(r => !r[this.mergedData.bulkActions.disableKey]);
        } else {
          this.selectedRows = this.rows;
        }
      } else {
        this.selectedRows = [];
      }

      this.mergedData.bulkActions.onSelect(this.selectedRows);
    },

    setSelectedRows(rows) {
      if (!rows || !Array.isArray(rows)) return;
      const ids = rows.map((r) => r.id);
      this.selectedRows = this.rows.filter((r) => ids.includes(r.id));
    },

    addRow: function() {
      this.mergedData.addRow.onClick(this.parameters);
    },

    removeRow: function(row) {
      this.mergedData.removeRow.onClick(row);
    },

    onClickOutsideColumnFilters: function() {
      if (this.showingColumnFilter == false) {
        this.showColumnFilter = '';
      }
    },

    onDisplayFilters: function(column) {
      this.showingColumnFilter = true;

      setTimeout(function() {
        if (this.showColumnFilter != column) {
          this.showColumnFilter = column;
        }

        this.showingColumnFilter = false;
        this.$nextTick(() => {
          const header = this.$refs['column_' + column][0];
          const filters = this.$refs['datatable-header-filter-' + column][0].$el;
          const { top, left, height } = header.getBoundingClientRect();
          const { width } = filters.getBoundingClientRect();

          filters.style.top = (top + height) + 'px';
          filters.style.left = (left - (width/2)) + 'px';
        })

      }.bind(this), 20);
    },

    filterRows: function(field, option) {
      this.appliedFilters[field] = option;

      const rows = this.mergedData.body.filter(function(row) {
        const checks = Object.keys(this.appliedFilters).map(filter => {
          return this.appliedFilters[filter] == null || valueFromString(row, filter) == this.appliedFilters[filter].value;
        });

        return _.every(checks);
      }.bind(this));

      // this.rows = rows;
    },

    applyFilterLabel: function(field, option) {
      this.appliedFilters[field] = option;
    },

    setTableWrapperWidth: function(mounted) {
      if (mounted) {
        window.addEventListener('resize', this.setTableWrapperWidth)
      }
      this.tableWrapperWidth = this.$refs.datatableWrapper.clientWidth;
    },

    setTableWrapperScroll: function() {
      const wrapper = this.$refs.datatableWrapper;
      const table = this.$refs.datatable;

      const { height } = table.getBoundingClientRect();
      const wrapperClientRect = wrapper.getBoundingClientRect();
      const { scrollTop } = wrapper;
      const margin = 100;

      // Check if vertical scroll position has changed
      if (scrollTop !== this.lastScrollTop) {
        this.lastScrollTop = scrollTop;

        if (scrollTop + wrapperClientRect.height + margin >= height) {
          this.$emit('onReachEnd');
        }
      }
    },
    onResize(data) {
      let  changedItem;

      if (_.get(data, 'explicitOriginalTarget.offsetParent'))
        changedItem = data.explicitOriginalTarget.offsetParent;

      if (data && data.path && data.path.length && data.path[1])
        changedItem = data.path[1];

      if(changedItem){
        const column = changedItem.getAttribute('data-column');

        const chengedColumn = {
            column: column,
            width: changedItem.offsetWidth,
            index: this.mergedData.columns.findIndex(item => item.key ===  column)
          }

        const selectedColumnIndex = this.mergedData.columns.findIndex(item => {
          return item.key === column;
        });

        this.preferences.widths = this.mergedData.fixedColumns.widths;

        if (selectedColumnIndex+1 <= this.preferences.widths.length) {
          this.preferences.widths[selectedColumnIndex] = changedItem.offsetWidth;
        } else {
          let widths = [];

          for (let i = 0; i < selectedColumnIndex; i++) {
            let newItem = null;

            if (this.preferences.widths[i]) {
              newItem = this.preferences.widths[i];
            }
            widths.push(newItem);
          }

          widths.push(chengedColumn.width);

          this.preferences.widths = widths;
        }

        if (this.savePreferences) {
          this.updateUserPreferences({
            name: this.getPreferenceCode('column_size'),
            settings: { widths: this.preferences.widths }
          });
        }
      }
    },
    getPreferenceCode(code) {
      if (this.code) code = `${this.code}_${code}`;

      return code;
    },

    displayValue (content) {
      return typeof content !== "number" && !content ? `<span class="no-content">${this.$t('datatable.no_content')}</span>` : content;
    },

    onDragRowStart(e, row, index) {
      // Create an empty image element to use as the drag image
      const emptyImage = new Image();
      emptyImage.src = EMPTY_IMAGE;

      // Set the drag image to the empty image
      e.dataTransfer.setDragImage(emptyImage, 0, 0);

      e.dataTransfer.effectAllowed = 'move';

      this.draggedData = { row, index };
    },

    onDragRowEnter (e, row, index) {
      // Current row positions that we want to switch
      const source = this.draggedData.index;
      const target = index;

      const draggingOverSelf = source === target;      
      if (!draggingOverSelf) {
        const data = { ...this.rowsIndexes };

        const sourceRowId = this.draggedData.row.row_id;
        const targetRowId = row.row_id;

        const sourceRowValue = typeof data[sourceRowId] === 'undefined' ? source : data[sourceRowId];
        const targetRowValue = typeof data[targetRowId] === 'undefined' ? target : data[targetRowId];

        data[sourceRowId] = targetRowValue;
        data[targetRowId] = sourceRowValue;

        this.draggedData = { row: { ...this.draggedData.row, index: target }, index: target };
        this.rowsIndexes = data;
      }      
    },

    onDragRowEnd (e) {
      this.data.reorderRows.onReorder(this.rows);
      this.draggedData = null;
    },

    reattachDragEventListeners() {
      // After dragEnter the DOM gets updated,
      // so if we don't reattach the event listeners for dragEnd it will not be triggered
      const rows = this.$el.querySelectorAll('tr .row-drag-handler[draggable="true"]');
      rows.forEach(row => {
        row.addEventListener('dragend', this.onDragRowEnd);
      });
    }
  },

  mixins: [propsForMixin, resizableTable],
}
</script>

<style lang="scss">
@import '../styles/components/datatable';

.table-top-area {
  display: flex;
  justify-content: flex-end;
  gap: 10px
}
.table-col-drag-handle {
  margin-right: 10px;
}
.min-width-200 {
  min-width: 200px;
}
</style>


